/**
 * API Service
 * 
 * Handles all HTTP requests to the backend API.
 * Provides methods for:
 * - Kink CRUD operations
 * - Sex Actions CRUD operations
 * - CSV export
 * 
 * All methods return promises and handle errors consistently.
 * 
 * Usage:
 *   import api from './services/api';
 *   const kinks = await api.getKinks({ search: 'bondage' });
 */

// API base URL - uses proxy in development, VITE_API_URL for production (Cloudflare Pages)
// In development: Vite proxies /api to localhost:3001
// In production: VITE_API_URL points to DO backend (e.g., https://api.pleasance.app)
const API_BASE = import.meta.env.VITE_API_URL
  ? `${import.meta.env.VITE_API_URL}/api`
  : '/api';

/**
 * Generic fetch wrapper with error handling
 * @param {string} endpoint - API endpoint path
 * @param {Object} options - Fetch options
 * @returns {Promise<any>} - Parsed JSON response
 */
async function fetchAPI(endpoint, options = {}) {
  const url = `${API_BASE}${endpoint}`;

  const headers = {
    ...options.headers
  };

  if (options.body && !(options.body instanceof FormData) && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  const config = {
    headers,
    ...options
  };

  try {
    const response = await fetch(url, config);

    // Handle non-JSON responses (like CSV downloads)
    const contentType = response.headers.get('content-type');

    // 1. Handle CSV downloads
    if (contentType && contentType.includes('text/csv')) {
      if (!response.ok) throw new Error('Failed to download CSV');
      return response.blob();
    }

    // 2. Handle JSON responses
    if (contentType && contentType.includes('application/json')) {
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || data.message || `HTTP ${response.status}`);
      }
      return data;
    }

    // 3. Handle non-JSON errors (e.g. 404 HTML pages, 500 server errors)
    if (!response.ok) {
      const text = await response.text();
      // Try to extract a meaningful error if it's HTML, otherwise just use status
      console.error(`API Error [${endpoint}]: received ${contentType}`, text.substring(0, 200));
      throw new Error(`Server returned ${response.status} ${response.statusText} (non-JSON response)`);
    }

    // 4. Unexpected success type
    return await response.text();

  } catch (error) {
    // Improve error logging
    console.group(`API Request Failed: ${endpoint}`);
    console.error(error);
    console.groupEnd();
    throw error;
  }
}

/**
 * API Service Object
 * Contains all API methods organized by feature
 */
const api = {

  // ===========================================================================
  // HEALTH CHECK
  // ===========================================================================

  /**
   * Check if the API is running
   * @returns {Promise<Object>} - Health status
   */
  async health() {
    return fetchAPI('/health');
  },

  // ===========================================================================
  // KINKS
  // ===========================================================================

  /**
   * Get paginated list of kinks with optional filters
   * @param {Object} params - Query parameters
   * @param {string} params.search - Search term
   * @param {string} params.category - Filter by category
   * @param {number} params.minWeirdness - Min weirdness score
   * @param {number} params.maxWeirdness - Max weirdness score
   * @param {boolean} params.processed - Filter by processed status
   * @param {number} params.page - Page number
   * @param {number} params.limit - Items per page
   * @param {string} params.sortBy - Sort field
   * @param {string} params.sortOrder - Sort direction
   * @returns {Promise<Object>} - { kinks, pagination }
   */
  async getKinks(params = {}) {
    const queryString = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== '')
    ).toString();

    return fetchAPI(`/kinks${queryString ? `?${queryString}` : ''}`);
  },

  /**
   * Get single kink with full details
   * @param {string} id - Kink UUID
   * @returns {Promise<Object>} - Kink with subkinks and connections
   */
  async getKink(id) {
    return fetchAPI(`/kinks/${id}`);
  },

  /**
   * Get all unique categories
   * @returns {Promise<Array>} - Array of category strings
   */
  async getCategories() {
    return fetchAPI('/kinks/categories');
  },

  /**
   * Get full database stats (total, intensity distribution, category counts)
   * @returns {Promise<Object>} - Stats object with totalItems, intensityCounts, categoryCounts
   */
  async getStats() {
    return fetchAPI('/kinks/stats');
  },

  /**
   * Create a new kink manually
   * @param {Object} kinkData - Kink data
   * @returns {Promise<Object>} - Created kink
   */
  async createKink(kinkData) {
    return fetchAPI('/kinks', {
      method: 'POST',
      body: JSON.stringify(kinkData)
    });
  },

  /**
   * Update an existing kink
   * @param {string} id - Kink UUID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} - Updated kink
   */
  async updateKink(id, updates) {
    return fetchAPI(`/kinks/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates)
    });
  },

  /**
   * Delete a kink
   * @param {string} id - Kink UUID
   * @returns {Promise<Object>} - Deletion confirmation
   */
  async deleteKink(id) {
    return fetchAPI(`/kinks/${id}`, {
      method: 'DELETE'
    });
  },

  // ===========================================================================
  // SEX ACTIONS
  // ===========================================================================

  /**
   * Get all sex actions with optional filtering
   * @param {Object} params - Query parameters (search, category, etc.)
   * @returns {Promise<Object>} - { actions, totalCount, categories }
   */
  async getSexActions(params = {}) {
    const queryString = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== '')
    ).toString();

    return fetchAPI(`/sex-actions${queryString ? `?${queryString}` : ''}`);
  },

  /**
   * Get single sex action with details
   * @param {string} id - Action UUID
   * @returns {Promise<Object>} - Sex action with linked kinks
   */
  async getSexAction(id) {
    return fetchAPI(`/sex-actions/${id}`);
  },

  /**
   * Update a sex action
   * @param {string} id - Action UUID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} - Updated action
   */
  async updateSexAction(id, updates) {
    return fetchAPI(`/sex-actions/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates)
    });
  },

  /**
   * Delete a sex action
   * @param {string} id - Action UUID
   * @returns {Promise<Object>} - Deletion confirmation
   */
  async deleteSexAction(id) {
    return fetchAPI(`/sex-actions/${id}`, {
      method: 'DELETE'
    });
  },

  // ===========================================================================
  // EXPORT
  // ===========================================================================

  /**
   * Get scenarios as JSON (for preview)
   * @param {Object} params - Filter parameters
   * @returns {Promise<Object>} - Scenarios data
   */
  async getScenarios(params = {}) {
    const queryString = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== '')
    ).toString();

    return fetchAPI(`/scenarios${queryString ? `?${queryString}` : ''}`);
  },

  /**
   * Get single scenario
   * @param {string} id - Scenario UUID
   * @returns {Promise<Object>} - Scenario details
   */
  async getScenario(id) {
    return fetchAPI(`/scenarios/${id}`);
  },

  /**
   * Update a scenario
   * @param {string} id - Scenario UUID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} - Updated scenario
   */
  async updateScenario(id, updates) {
    return fetchAPI(`/scenarios/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates)
    });
  },

  /**
   * Delete a scenario
   * @param {string} id - Scenario UUID
   * @returns {Promise<Object>} - Deletion confirmation
   */
  async deleteScenario(id) {
    return fetchAPI(`/scenarios/${id}`, {
      method: 'DELETE'
    });
  },

  /**
   * Download scenarios as CSV
   * @param {Object} params - Filter parameters
   * @returns {Promise<Blob>} - CSV blob for download
   */
  async exportScenariosCSV(params = {}) {
    const queryString = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== '')
    ).toString();

    const blob = await fetchAPI(`/export/scenarios${queryString ? `?${queryString}` : ''}`);

    // Trigger download
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `kink-scenarios-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    return true;
  },

  // ===========================================================================
  // SEARCH
  // ===========================================================================

  /**
   * Unified search across all types
   * @param {string} query - Search term
   * @param {string} type - Filter by type (all/kinks/actions/archetypes)
   * @param {number} limit - Max results
   * @returns {Promise<Object>} - Search results
   */
  async search(query, type = 'all', limit = 30) {
    return fetchAPI(`/search?q=${encodeURIComponent(query)}&type=${type}&limit=${limit}`);
  },

  // ===========================================================================
  // ARCHETYPES
  // ===========================================================================

  /**
   * Get all archetypes
   * @param {string|null} category - Optional category filter
   * @param {string|null} query - Optional search query
   * @returns {Promise<Array>} - List of archetypes
   */
  async getArchetypes(params = {}) {
    const queryString = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== '' && v !== null)
    ).toString();

    return fetchAPI(`/archetypes${queryString ? `?${queryString}` : ''}`);
  },

  /**
   * Get single archetype
   * @param {string} id - Archetype UUID
   * @returns {Promise<Object>} - Archetype details
   */
  async getArchetype(id) {
    return fetchAPI(`/archetypes/${id}`);
  },

  // ===========================================================================
  // AROUSAL MECHANICS & EFFECTS
  // ===========================================================================

  /**
   * Get all arousal mechanics
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} - { mechanics, pagination }
   */
  async getArousalMechanics(params = {}) {
    const queryString = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== '')
    ).toString();

    return fetchAPI(`/arousal-mechanics${queryString ? `?${queryString}` : ''}`);
  },

  /**
   * Get single arousal mechanic
   * @param {string} id - Mechanic UUID
   * @returns {Promise<Object>} - Mechanic details
   */
  async getArousalMechanic(id) {
    return fetchAPI(`/arousal-mechanics/${id}`);
  },

  /**
   * Update an arousal mechanic
   * @param {string} id - Mechanic UUID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} - Updated mechanic
   */
  async updateArousalMechanic(id, updates) {
    return fetchAPI(`/arousal-mechanics/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates)
    });
  },

  /**
   * Delete an arousal mechanic
   * @param {string} id - Mechanic UUID
   * @returns {Promise<Object>} - Deletion confirmation
   */
  async deleteArousalMechanic(id) {
    return fetchAPI(`/arousal-mechanics/${id}`, {
      method: 'DELETE'
    });
  },

  /**
   * Get all arousal effects
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} - { effects, pagination }
   */
  async getArousalEffects(params = {}) {
    const queryString = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== '')
    ).toString();

    return fetchAPI(`/arousal-effects${queryString ? `?${queryString}` : ''}`);
  },

  /**
   * Get single arousal effect
   * @param {string} id - Effect UUID
   * @returns {Promise<Object>} - Effect details
   */
  async getArousalEffect(id) {
    return fetchAPI(`/arousal-effects/${id}`);
  },

  /**
   * Export kinks as CSV
   * @param {Object} params - Filter parameters
   * @returns {Promise<Blob>} - CSV blob for download
   */
  async exportKinksCSV(params = {}) {
    const queryString = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== '')
    ).toString();

    const blob = await fetchAPI(`/export/kinks${queryString ? `?${queryString}` : ''}`);

    // Trigger download
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `kink-database-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    return true;
  },

  // ===========================================================================
  // GEAR
  // ===========================================================================

  /**
   * Get all gear with optional filtering
   * @param {Object} params - Query parameters (search, category, etc.)
   * @returns {Promise<Object>} - { gear, totalCount, categories }
   */
  async getGear(params = {}) {
    const queryString = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== '')
    ).toString();

    return fetchAPI(`/gear${queryString ? `?${queryString}` : ''}`);
  },

  /**
   * Get single gear item with details
   * @param {string} id - Gear UUID
   * @returns {Promise<Object>} - Gear with linked kinks/actions
   */
  async getGearById(id) {
    return fetchAPI(`/gear/${id}`);
  },

  /**
   * Get all unique gear categories
   * @returns {Promise<Array>} - Array of category strings
   */
  async getGearCategories() {
    return fetchAPI('/gear/categories');
  },

  /**
   * Create new gear
   * @param {Object} gearData - Gear data
   * @returns {Promise<Object>} - Created gear
   */
  async createGear(gearData) {
    return fetchAPI('/gear', {
      method: 'POST',
      body: JSON.stringify(gearData)
    });
  },

  /**
   * Update gear
   * @param {string} id - Gear UUID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} - Updated gear
   */
  async updateGear(id, updates) {
    return fetchAPI(`/gear/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates)
    });
  },

  /**
   * Delete gear
   * @param {string} id - Gear UUID
   * @returns {Promise<Object>} - Deletion confirmation
   */
  async deleteGear(id) {
    return fetchAPI(`/gear/${id}`, {
      method: 'DELETE'
    });
  },

  // ===========================================================================
  // CHARACTERS
  // ===========================================================================

  /**
   * Get all characters with optional filtering
   * @param {Object} params - Query parameters (q, source_type, limit)
   * @returns {Promise<Object>} - { characters, sourceTypes, totalCount }
   */
  async getCharacters(params = {}) {
    const queryString = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== '')
    ).toString();

    return fetchAPI(`/characters${queryString ? `?${queryString}` : ''}`);
  },

  /**
   * Get single character
   * @param {string} id - Character UUID
   * @returns {Promise<Object>} - Character with traits and links
   */
  async getCharacter(id) {
    return fetchAPI(`/characters/${id}`);
  },

  // ===========================================================================
  // POSITIONS
  // ===========================================================================

  /**
   * Get all positions with optional filtering
   * @param {Object} params - Query parameters (q, category, difficulty, limit)
   * @returns {Promise<Object>} - { positions, categories, totalCount }
   */
  async getPositions(params = {}) {
    const queryString = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== '')
    ).toString();

    return fetchAPI(`/positions${queryString ? `?${queryString}` : ''}`);
  },

  /**
   * Get single position
   * @param {string} id - Position UUID
   * @returns {Promise<Object>} - Position details
   */
  async getPosition(id) {
    return fetchAPI(`/positions/${id}`);
  },

  // ===========================================================================
  // BODY PARTS
  // ===========================================================================

  /**
   * Get all body parts with optional filtering
   * @param {Object} params - Query parameters (q, category, zone_type, limit)
   * @returns {Promise<Object>} - { bodyParts, categories, totalCount }
   */
  async getBodyParts(params = {}) {
    const queryString = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== '')
    ).toString();

    return fetchAPI(`/body-parts${queryString ? `?${queryString}` : ''}`);
  },

  /**
   * Get single body part
   * @param {string} id - Body part UUID
   * @returns {Promise<Object>} - Body part details
   */
  async getBodyPart(id) {
    return fetchAPI(`/body-parts/${id}`);
  },

  // ===========================================================================
  // DYNAMICS
  // ===========================================================================

  /**
   * Get all dynamics with optional filtering
   * @param {Object} params - Query parameters (q, category, type, limit)
   * @returns {Promise<Object>} - { dynamics, categories, totalCount }
   */
  async getDynamics(params = {}) {
    const queryString = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== '')
    ).toString();

    return fetchAPI(`/dynamics${queryString ? `?${queryString}` : ''}`);
  },

  /**
   * Get single dynamic
   * @param {string} id - Dynamic UUID
   * @returns {Promise<Object>} - Dynamic details
   */
  async getDynamic(id) {
    return fetchAPI(`/dynamics/${id}`);
  },

  // ===========================================================================
  // ACTIVITIES
  // ===========================================================================

  /**
   * Get all activities with optional filtering
   * @param {Object} params - Query parameters (q, category, player_count, limit)
   * @returns {Promise<Object>} - { activities, categories, totalCount }
   */
  async getActivities(params = {}) {
    const queryString = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v != null)
    ).toString();

    return fetchAPI(`/activities${queryString ? `?${queryString}` : ''}`);
  },

  /**
   * Get single activity
   * @param {string} id - Activity UUID
   * @returns {Promise<Object>} - Activity details
   */
  async getActivity(id) {
    return fetchAPI(`/activities/${id}`);
  }
};

export default api;
